import type { Brand } from '../brand';

interface BrandCopy {
  cancelOrderPrimaryAction: string;
  cancelOrderWarningDescription: string;
  cancelOrderSuccessDescription: string;
  /** Shown to the customer and submitted as the reason value, so these strings are part
   *  of the cancellation contract. */
  switchVehicleCancelReason: 'Switching to another Carvana car' | 'Switching to another car';
  tradeOfferLabel: string;
  addressChangeConfirmAction: string;
  financeHeadline: string;
  financeSubtitle: string;
  useFinancingAction: string;
  deliveryLabel: string;
  gettingVehicleHeading: string;
  nonrefundableShippingLabel: string;
  duplicatePurchaseTitle: string;
  duplicatePurchaseSubtitle: string;
  /** "Car Vending Machine" is Carvana's product name, meaningless to a Slate customer. */
  vendingMachineName: string;
  editVehicleAction: string;
  deliveryAddressLabel: string;
  vendingMachineExperienceDescription: string;
  homeDeliveryDescription: string;
  /** Order summary row label and edit link for a delivered vehicle. */
  deliverySummaryLabel: string;
  editDeliveryAction: string;
  /** Bold lead-in and regular tail of the finance tile's credit-impact bullet. */
  financeNoCreditImpactLead: string;
  financeNoCreditImpactTail: string;
}

const BRAND_COPY: Record<Brand, BrandCopy> = {
  carvana: {
    cancelOrderPrimaryAction: 'Search vehicles',
    cancelOrderWarningDescription:
      'This vehicle will be immediately available for other buyers and cannot be undone.',
    cancelOrderSuccessDescription:
      "Sorry this one didn't work out. If you're still looking for a car, check out our other vehicles.",
    switchVehicleCancelReason: 'Switching to another Carvana car',
    tradeOfferLabel: 'Your Carvana offer',
    addressChangeConfirmAction: 'Continue with address change',
    financeHeadline: 'Finance with Carvana',
    financeSubtitle:
      'Your Carvana financing terms are based on your credit history and price of this vehicle.',
    useFinancingAction: 'Use Carvana financing',
    deliveryLabel: 'Delivering to',
    gettingVehicleHeading: 'Getting your vehicle',
    nonrefundableShippingLabel: 'Nonrefundable shipping',
    duplicatePurchaseTitle: 'Which car would you like to purchase?',
    duplicatePurchaseSubtitle:
      'We noticed that you already started a purchase. Select the car you would like to proceed with.',
    vendingMachineName: 'Car Vending Machine',
    editVehicleAction: 'Edit vehicle',
    deliveryAddressLabel: 'Delivery address',
    vendingMachineExperienceDescription:
      'Just drop your Carvana coin in the slot, watch your vehicle descend and snap pics with your family and friends.',
    homeDeliveryDescription:
      "Let us come to you! We'll deliver your vehicle right to your doorstep.",
    deliverySummaryLabel: 'Delivery',
    editDeliveryAction: 'Edit delivery',
    financeNoCreditImpactLead: 'No hit',
    financeNoCreditImpactTail: 'to your credit score'
  },
  slate: {
    cancelOrderPrimaryAction: 'Continue',
    cancelOrderWarningDescription: 'You will be able to start a new order at any time.',
    cancelOrderSuccessDescription: "Sorry this one didn't work out.",
    switchVehicleCancelReason: 'Switching to another car',
    tradeOfferLabel: 'Your offer',
    addressChangeConfirmAction: 'Continue with change',
    financeHeadline: 'Finance with Slate',
    financeSubtitle:
      "Your Slate financing terms are based on your credit history, this vehicle's details and your selected accessories.",
    useFinancingAction: 'Use Slate financing',
    deliveryLabel: 'Delivering Slate to',
    gettingVehicleHeading: 'Getting your vehicle and accessories',
    nonrefundableShippingLabel: 'Nonrefundable vehicle shipping',
    duplicatePurchaseTitle: 'You already have a purchase in progress',
    duplicatePurchaseSubtitle:
      "Slate checkout is powered by Carvana, and you have a Carvana purchase that's still in progress. Choose the car you'd like to move forward with.",
    vendingMachineName: 'Carvana Vending Machine',
    editVehicleAction: 'Edit Slate',
    deliveryAddressLabel: 'Vehicle delivery address',
    vendingMachineExperienceDescription:
      'Just drop your Carvana coin in the slot, watch your Slate descend and snap pics with your family and friends.',
    homeDeliveryDescription: "Let us come to you! We'll deliver your Slate right to your doorstep.",
    deliverySummaryLabel: 'Vehicle delivery',
    editDeliveryAction: 'Edit Vehicle Delivery',
    financeNoCreditImpactLead: 'No credit impact',
    financeNoCreditImpactTail: 'to get prequalified'
  }
};

export { BRAND_COPY };
export type { BrandCopy };
